import { RefObject, useCallback, useEffect, useState } from "react";
import { PlanTab } from "v2/components/billing/CoreGrowthSegmentedControl";

// Two-way sync between the horizontal card strip and the Core/Growth tab control: clicking a tab
// scrolls the strip; free-scrolling or arrow keys update the active tab. `coreCount` marks where
// Growth begins (el.children[coreCount] is the first growth card).
export function useScrollStripTabs(scrollerRef: RefObject<HTMLElement>, coreCount: number) {
  const [activeTab, setActiveTab] = useState<PlanTab>("Core");

  // Core anchors to the left edge; Growth anchors to its first card.
  const scrollToTab = useCallback((tab: PlanTab) => {
    const el = scrollerRef.current;
    if (!el) return;
    const firstGrowth = el.children[coreCount] as HTMLElement | undefined;
    el.scrollTo({
      left: tab === "Growth" ? (firstGrowth?.offsetLeft ?? el.scrollWidth - el.clientWidth) : 0,
      behavior: "smooth",
    });
    setActiveTab(tab);
  }, [ scrollerRef, coreCount ]);

  const switchToOtherTab = useCallback(() => {
    scrollToTab(activeTab === "Core" ? "Growth" : "Core");
  }, [ activeTab, scrollToTab ]);

  // Keep the tab control in sync while free-scrolling.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScrollEnd = () => {
      const firstGrowth = el.children[coreCount] as HTMLElement | undefined;
      const boundary = firstGrowth?.offsetLeft ?? Infinity;
      const viewportCentre = el.scrollLeft + el.clientWidth / 2;
      setActiveTab(viewportCentre >= boundary ? "Growth" : "Core");
    };
    el.addEventListener("scrollend", onScrollEnd);
    return () => el.removeEventListener("scrollend", onScrollEnd);
  }, [ scrollerRef, coreCount ]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "ArrowRight" && activeTab === "Core") switchToOtherTab();
      if (e.key === "ArrowLeft" && activeTab === "Growth") switchToOtherTab();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [ activeTab, switchToOtherTab ]);

  return { activeTab, scrollToTab, switchToOtherTab };
}
